import React, { useEffect, useState } from "react";

import { ArrowRight, Loader2 } from "lucide-react";
import { createPortal } from "react-dom";

import {
  buildCustomerStakeholderApiLink,
  matrixPairsToApiLinks,
  stakeholderApiLinkPairKey,
} from "./stakeholderMappingUtils";
import { useToast } from "../../../Components/Toast";
import { useAppDispatch } from "../../../Store/Hooks";
import { saveAccountStakeholderRelationships } from "../../../Store/Reducers/RelationshipSlice";

import "./relationship-prototype-skin.css";

function MapThisRelationshipModal({
  open,
  account,
  stakeholder,
  contact,
  pairs = [],
  stakeholders = [],
  contacts = [],
  onClose,
  onOpenCoverageMap,
  onMapped,
}) {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    function onKey(event) {
      if (event.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open || !account || !stakeholder || !contact) return null;

  const vendorName = stakeholder.name || "Vendor";
  const contactName = contact.name || "Contact";
  const accountName = account.name || "Account";
  const newLink = buildCustomerStakeholderApiLink(stakeholder, contact);
  const missingBridge = !newLink;

  const handleMapAndAssess = async () => {
    if (!newLink || !account.id) {
      toast.error(
        "This contact is not linked to customer team data yet. Open Coverage Map or sync contacts first."
      );
      return;
    }

    setSaving(true);
    try {
      const links = matrixPairsToApiLinks(pairs, stakeholders, contacts);

      const newKey = stakeholderApiLinkPairKey(newLink);
      if (!links.some(link => stakeholderApiLinkPairKey(link) === newKey)) {
        links.push(newLink);
      }

      await dispatch(
        saveAccountStakeholderRelationships({
          accountId: account.id,
          links,
        })
      ).unwrap();

      toast.success(
        `Mapped: ${vendorName} ↔ ${contactName} — ready to assess.`
      );
      onClose();
      await onMapped?.({ stakeholder, contact });
    } catch (err) {
      console.error("Map this relationship failed:", err);
      toast.error(
        err?.response?.data?.error ||
          err?.message ||
          "Failed to map this relationship"
      );
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="rl-prototype-skin">
      <div
        id="modal-map-pair"
        role="presentation"
        className="modal-bg open"
        onClick={event => {
          if (event.target === event.currentTarget) onClose();
        }}
      >
        <article
          className="modal"
          style={{ maxWidth: 420 }}
          role="dialog"
          aria-labelledby="map-pair-title"
          onClick={event => event.stopPropagation()}
        >
          <header className="modal-hdr">
            <div className="mh-info">
              <h2 id="map-pair-title" className="modal-title">
                Map This Relationship
              </h2>
              <p className="modal-sub">
                {accountName}
                {" · "}
                {vendorName}
                {" ↔ "}
                {contactName}
              </p>
            </div>
            <button
              type="button"
              className="modal-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </header>

          <div className="modal-body">
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: "10px 12px",
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 6,
                marginBottom: 14,
              }}
            >
              <svg
                style={{ flexShrink: 0, marginTop: 1, color: "#16a34a" }}
                width="16"
                height="16"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v3.586L7.707 9.293a1 1 0 00-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 10.586V7z"
                  clipRule="evenodd"
                />
              </svg>
              <div style={{ fontSize: 12, color: "#166534", lineHeight: 1.5 }}>
                This pair is not yet in the coverage map. Mapping establishes
                that this executive–contact relationship exists and should be
                assessed.{" "}
                <strong>
                  You can map just this pair now, or open the full Coverage Map
                  to map multiple pairs at once.
                </strong>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 12px",
                background: "var(--gray-50)",
                border: "1px solid var(--border)",
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 600,
                color: "var(--gray-800)",
                marginBottom: 8,
              }}
            >
              <span
                style={{
                  padding: "2px 8px",
                  background: "var(--brand-faint)",
                  borderRadius: 4,
                  color: "var(--brand)",
                  fontSize: 12,
                }}
              >
                {vendorName}
              </span>
              <ArrowRight
                size={14}
                style={{ color: "var(--text-muted)", flexShrink: 0 }}
                aria-hidden
              />
              <span
                style={{
                  padding: "2px 8px",
                  background: "#f0fdf4",
                  borderRadius: 4,
                  color: "#15803d",
                  fontSize: 12,
                }}
              >
                {contactName}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontSize: 10,
                  color: "var(--text-muted)",
                  fontWeight: 400,
                }}
              >
                {accountName}
              </span>
            </div>

            {missingBridge ? (
              <div
                style={{
                  fontSize: 11,
                  color: "#92400e",
                  padding: "6px 10px",
                  background: "#fef3c7",
                  border: "1px solid #fde68a",
                  borderRadius: 5,
                  lineHeight: 1.4,
                }}
              >
                This contact is missing customer-team bridge data. Use{" "}
                <strong>Open Coverage Map</strong> after contacts are synced, or
                refresh the account matrix.
              </div>
            ) : null}
          </div>

          <footer className="modal-ftr" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-sec"
              onClick={onClose}
              style={{ flex: "none" }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-sec"
              onClick={() => {
                onClose();
                onOpenCoverageMap?.();
              }}
              style={{ flex: 1 }}
            >
              Open Coverage Map
            </button>
            <button
              type="button"
              className="btn btn-pri"
              onClick={handleMapAndAssess}
              disabled={saving || missingBridge}
              style={{ flex: 1 }}
            >
              {saving ? (
                <>
                  <Loader2
                    className="inline animate-spin"
                    style={{ width: 14, height: 14, marginRight: 6 }}
                  />
                  Mapping…
                </>
              ) : (
                "Map & Assess →"
              )}
            </button>
          </footer>
        </article>
      </div>
    </div>,
    document.body
  );
}

export default MapThisRelationshipModal;
